import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { SkeletonStatStrip } from '@/shared/components/Skeleton'
import { PolicyKpiStrip } from '@/features/policies/components/PolicyKpiStrip'
import { useOrgDecisionLedger, useOrgPolicyCoverage, useOrgPolicySettings } from '@/lib/hooks'
import { policiesFired } from '@/features/policies/lib/decisionView'

/**
 * The policy KPIs, fetched for the dashboard.
 *
 * Three reader-visible endpoints, not the analytics payload: refusals and overrides come from the
 * decision ledger's facets, "blocked now" from the policy settings' advisory counts, and the fired
 * ratio from coverage. Deliberately NOT folded into /analytics - these are the same reads the
 * Policies page makes, so a figure can never disagree between the two pages.
 *
 * The ledger is asked for ONE row: `facets` is computed over the whole window regardless of the
 * page, and nothing here renders a row.
 *
 * An org with no policies at all gets a sentence rather than four zeroes - "0 refusals" reads as
 * "nothing got through" when the truth is "nothing was checked", which is the opposite.
 */
export function PolicyPostureStrip({ orgSlug }: { orgSlug: string }) {
  const ledger   = useOrgDecisionLedger(orgSlug, {}, 1)
  const coverage = useOrgPolicyCoverage(orgSlug)
  const settings = useOrgPolicySettings(orgSlug)

  // No bottom margin: the enclosing PageSection owns the gap to the next section now, and
  // doubling it left the strip floating well clear of its own heading.
  const wrap = (children: ReactNode) => <div>{children}</div>

  if (ledger.loading || coverage.loading || settings.loading) return wrap(<SkeletonStatStrip />)

  // A failed secondary fetch must not blank the lead of the dashboard, and must not be silent
  // either: a missing strip would read as "nothing to report".
  if (ledger.error || coverage.error || settings.error) {
    return wrap(
      <p style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem', color: 'var(--text3)', margin: 0 }}>
        // policy posture unavailable — {ledger.error ?? coverage.error ?? settings.error}
      </p>,
    )
  }

  const cov = coverage.data?.coverage ?? []
  const fired = policiesFired(cov)
  const facets = ledger.facets

  if (fired.total === 0) {
    return wrap(
      <p style={{ fontSize: '0.8rem', color: 'var(--text2)', margin: 0 }}>
        No policies are authored yet, so nothing is being refused.{' '}
        <Link to={`/org/${orgSlug}/policies`}>Add the recommended set →</Link>
      </p>,
    )
  }

  return wrap(
    <PolicyKpiStrip
      blocks={facets?.verdict.find((v) => v.key === 'block')?.n ?? 0}
      overrides={facets?.verdict.find((v) => v.key === 'override')?.n ?? 0}
      settings={settings.data ?? null}
      fired={fired}
      windowDays={ledger.windowDays}
      policiesPath={`/org/${orgSlug}/policies`}
    />,
  )
}
